import React from 'react';
import { createAuthorizationFailureLifecycle } from './authorizationFailureLifecycle';
import { createCineResourceGovernor } from './cineResourceGovernor';

const createDefaultGovernor = ({ onAuthorizationFailure }) => createCineResourceGovernor({ onAuthorizationFailure });
const scheduleDefaultDestroy = callback => window.setTimeout(callback, 0);
const cancelDefaultDestroy = handle => window.clearTimeout(handle);

export const useViewerPageCineLifecycle = ({
  logout,
  navigate,
  createGovernor = createDefaultGovernor,
  createAuthorizationLifecycle = createAuthorizationFailureLifecycle,
  scheduleDestroy = scheduleDefaultDestroy,
  cancelDestroy = cancelDefaultDestroy,
} = {}) => {
  const authorizationLifecycleRef = React.useRef(null);
  if (!authorizationLifecycleRef.current) {
    authorizationLifecycleRef.current = createAuthorizationLifecycle();
  }
  const authorizationLifecycle = authorizationLifecycleRef.current;
  const currentHandlersRef = React.useRef({ logout, navigate });
  currentHandlersRef.current = { logout, navigate };
  authorizationLifecycle.update(currentHandlersRef.current);

  const governorRef = React.useRef(null);
  if (!governorRef.current) {
    governorRef.current = createGovernor({
      onAuthorizationFailure: authorizationLifecycle.handle,
    });
  }
  const governor = governorRef.current;
  const deferredDestroyRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (deferredDestroyRef.current != null) {
      cancelDestroy(deferredDestroyRef.current);
      deferredDestroyRef.current = null;
    }
    // StrictMode replays setup after cleanup without another render. Restore the
    // current stable auth handlers here as well as during ordinary renders.
    authorizationLifecycle.update(currentHandlersRef.current);

    return () => {
      // Final unmount and StrictMode replay both fail closed synchronously. The
      // deferred destroy is cancelled by replayed setup, but survives a real
      // unmount and permanently releases the page governor exactly once.
      authorizationLifecycle.clear();
      governor.reset('lifecycle-cleanup');
      let handle = null;
      handle = scheduleDestroy(() => {
        if (deferredDestroyRef.current !== handle) return;
        deferredDestroyRef.current = null;
        governor.destroy();
      });
      deferredDestroyRef.current = handle;
    };
  }, [authorizationLifecycle, cancelDestroy, governor, scheduleDestroy]);

  return governor;
};
